import { Button } from "$components/button/button";
import { ConditionalDisplay } from "$components/conditional-display";
import { makeStyles } from "$components/mui-styles";
import useCurrencyFormatter from "$routes/patient/[:patientId]/_lib/invoices/hooks/useCurrencyFormatter";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import SaveIcon from "@material-ui/icons/Save";
import React, { useEffect, useMemo, useState } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { FormattedMessage } from "react-intl";
import useUpdateInsurers from "../hooks/useUpdateInsurers";
import InsurerAccordion, {
  type Insurer,
  type Payer,
} from "../InsurerAccordion";
import { INSURERS, PRIVATE_INSURANCE } from "../InsurerAccordion/formConstants";
import {
  type FormValues,
  useValidationSchema as insurersSchema,
} from "../InsurerAccordion/formHelpers";
import type { PatientInsurance } from "../types";
import type { InvoiceDetailsDispatcher } from "./hooks/useInvoiceDetailsDispatcher";

const useStyles = makeStyles((theme) => ({
  insurerContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  addInsurersButton: {
    marginTop: theme.spacing(1),
  },
  header: {
    fontWeight: 700,
    fontSize: 12,
    color: theme.palette.grey[600],
    fontFamily: theme.typography.fontFamily,
    textTransform: "uppercase",
  },
}));

interface Props {
  canAddInsurers: boolean;
  hasLineItems: boolean;
  payers: Payer[];
  patientInsurance: PatientInsurance;
  totalAmount: number;
  invoiceId: string;
  editable: boolean;
  insurers: Insurer[];
  paid: number;
  setPaid: (paid: number) => void;
  isDraft: boolean;
  dispatcher: InvoiceDetailsDispatcher;
  busy?: boolean;
}

export const InsurerSection = ({
  canAddInsurers,
  hasLineItems,
  payers,
  totalAmount,
  invoiceId,
  patientInsurance,
  editable,
  insurers,
  paid,
  setPaid,
  isDraft,
  dispatcher,
  busy,
}: Props): React.ReactElement => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const { currencyFormatter } = useCurrencyFormatter();

  const insurersFormMethods = useForm<FormValues>({
    mode: "onTouched",

    resolver: yupResolver(insurersSchema(totalAmount)),
    defaultValues: { payers },
  });

  const { control, handleSubmit, formState, reset } = insurersFormMethods;

  const { isValid, isDirty } = formState;

  const { fields, append, remove } = useFieldArray({
    control,
    name: INSURERS,
  });

  // @ts-ignore
  const watchedInsurers: Payer[] = useWatch({ name: INSURERS, control });

  const availableInsurers = insurers.filter(
    (i) =>
      !watchedInsurers.find(
        (p) =>
          i.name === p?.insuranceDetails?.name ||
          (i.insuranceType === PRIVATE_INSURANCE && i.name === p.name),
      ),
  );

  const amountPaid = useMemo(() => {
    return watchedInsurers.reduce(
      (a: number, b: Payer) => a + b?.balance?.total || 0,
      0,
    );
  }, [watchedInsurers]);

  useEffect(() => {
    if (amountPaid !== paid) {
      setPaid(amountPaid);
    }
  }, [amountPaid, paid, setPaid]);

  const { updateInsurers } = useUpdateInsurers(watchedInsurers, invoiceId);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddInsurer = (insurer: Insurer) => {
    const patientHasInsurance =
      insurer.insuranceType === patientInsurance?.insuranceType;
    const insurerName =
      insurer.insuranceType !== PRIVATE_INSURANCE
        ? insurer.name
        : patientInsurance?.insuranceDetails?.schemeName || null;
    handleClose();
    append({
      type: insurer.insuranceType,
      name: insurer.name,
      balance: {
        total: 0,
      },
      properties: insurer.properties,
      insuranceDetails: {
        name: insurerName,
        insuranceNumber: patientHasInsurance
          ? patientInsurance?.insuranceDetails?.insuranceNumber
          : null,
        dependent:
          patientHasInsurance && insurer.insuranceType !== PRIVATE_INSURANCE
            ? patientInsurance?.insuranceDetails?.dependent
            : null,
        nationalIdNumber: patientHasInsurance
          ? patientInsurance?.insuranceDetails?.nationalIdNumber
          : null,
      },
    });
  };

  const handleRemoveInsurer = (index: number) => {
    remove(index);
  };

  const saveInsurers = async (formValues) => {
    dispatcher.sendBusy();
    try {
      await updateInsurers(formValues);
      // TODO: Why?
      reset(formValues);
    } finally {
      dispatcher.sendBusy(false);
    }
  };

  if (!hasLineItems) return null;

  return (
    <FormProvider {...insurersFormMethods}>
      <form name="InsurersForm" onSubmit={handleSubmit(saveInsurers)}>
        <Grid container className={classes.insurerContainer}>
          <Grid item xs={12}>
            <Typography variant="h6">
              <FormattedMessage
                id="invoicing.insuranceSection.title"
                defaultMessage="Insurance"
              />
            </Typography>
          </Grid>
          <ConditionalDisplay condition={!editable}>
            <Grid item xs={12}>
              <Box py={2}>
                <Divider />
              </Box>
              <ConditionalDisplay condition={watchedInsurers.length < 1}>
                <Box p={2} textAlign="center">
                  <Typography variant="subtitle1">
                    <FormattedMessage
                      id="invoicing.insuranceSection.noInsurers.title"
                      defaultMessage="No insurance"
                    />
                  </Typography>
                  <ConditionalDisplay condition={isDraft}>
                    <Typography variant="subtitle2">
                      <FormattedMessage
                        id="invoicing.insuranceSection.noInsurers.subtitle"
                        defaultMessage="Enable editing to add insurers"
                      />
                    </Typography>
                  </ConditionalDisplay>
                </Box>
              </ConditionalDisplay>
              <ConditionalDisplay condition={watchedInsurers.length > 0}>
                <Grid container spacing={2}>
                  <Grid item xs={8} className={classes.header}>
                    <Box px={2}>
                      <FormattedMessage
                        id="invoicing.insuranceSection.heading.insurer"
                        defaultMessage="Insurer"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4} className={classes.header}>
                    <Box px={2}>
                      <FormattedMessage
                        id="invoicing.insuranceSection.heading.Amount"
                        defaultMessage="Amount"
                      />
                    </Box>
                  </Grid>
                  {fields.map((insurer) => (
                    <React.Fragment
                      key={`${insurer.name}-${insurer.balance.total}`}
                    >
                      <Grid item xs={8}>
                        <Box px={2}>
                          <Typography>{insurer.name}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box px={2}>
                          <Typography>
                            {currencyFormatter(insurer.balance.total)}
                          </Typography>
                        </Box>
                      </Grid>
                    </React.Fragment>
                  ))}
                </Grid>
              </ConditionalDisplay>
            </Grid>
          </ConditionalDisplay>
          <ConditionalDisplay condition={editable}>
            <ConditionalDisplay condition={availableInsurers.length > 0}>
              <Grid item xs={12} className={classes.buttonContainer}>
                <Button
                  color="secondary"
                  onClick={handleClick}
                  startIcon={<AddIcon />}
                  className={classes.addInsurersButton}
                  disabled={!canAddInsurers}
                >
                  <FormattedMessage
                    id="invoicing.invoice.insurers.addInsurer"
                    defaultMessage="Add Insurer"
                  />
                </Button>
                <Menu
                  id="invoicing-insurer-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  {availableInsurers?.map((insurer) => (
                    <MenuItem
                      onClick={() => handleAddInsurer(insurer)}
                      key={insurer.id}
                    >
                      {insurer.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Grid>
            </ConditionalDisplay>
            <Divider />
            <ConditionalDisplay condition={fields?.length > 0}>
              {fields?.map((payer, index) => (
                <Grid item xs={12} key={payer?.name}>
                  <InsurerAccordion
                    index={index}
                    insurer={payer as unknown as Payer}
                    handleRemoveInsurer={handleRemoveInsurer}
                    insurers={insurers as Insurer[]}
                    invoiceId={invoiceId}
                  />
                </Grid>
              ))}
            </ConditionalDisplay>
            <Grid item xs={12}>
              <Box textAlign="right">
                <Button
                  type="submit"
                  color="secondary"
                  disabled={!isDirty || !isValid || busy}
                  startIcon={<SaveIcon />}
                  data-testid="invoicing.saveInsurers.button"
                >
                  <FormattedMessage
                    id="invoicing.invoice.saveInsurers"
                    defaultMessage="Save Insurers"
                  />
                </Button>
              </Box>
            </Grid>
          </ConditionalDisplay>
        </Grid>
      </form>
    </FormProvider>
  );
};
